import { useEffect, useState, type JSX } from "react"
import { IconButton } from "@mui/material"
import { Dock, Fullscreen, FullscreenExit, Close } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { useNotifications } from "hooks/notifications"
import type { IDockviewHeaderActionsProps } from "dockview"
import {
  dockFloatingGroupForMaximize,
  dockFloatingGroupToGrid,
  getFloatingDockMaximizeSession,
  restoreFloatingGroupAfterMaximizedDock,
} from "./dock-floating-for-maximize"
import { DOCKVIEW_LOCATION_TYPE_FLOATING } from "./types"

type RightControlsProps = IDockviewHeaderActionsProps & {
  persistLayout?: () => void
  closePanel?: ((panelId: string, requiresConfirmation?: boolean) => void) | null
  removeRequiresConfirmation?: boolean
}

export default function RightControls(props: RightControlsProps): JSX.Element {
  const { removeRequiresConfirmation = false } = props
  const notifications = useNotifications()
  const [locationType, setLocationType] = useState(() => props.api.location.type)
  const isFloatingGroup = locationType === DOCKVIEW_LOCATION_TYPE_FLOATING
  const [gridMaximized, setGridMaximized] = useState(() => props.api.isMaximized())
  const [dockButtonDismissed, setDockButtonDismissed] = useState(false)

  useEffect(() => {
    const disposable = props.api.onDidLocationChange(() => {
      const nextLocationType = props.api.location.type
      setLocationType(nextLocationType)
      if (nextLocationType !== DOCKVIEW_LOCATION_TYPE_FLOATING) {
        setDockButtonDismissed(false)
      }
    })
    return () => {
      disposable.dispose()
    }
  }, [props.api])

  useEffect(() => {
    const disposable = props.containerApi.onDidMaximizedGroupChange(() => {
      setGridMaximized(props.api.isMaximized())
    })

    return () => {
      disposable.dispose()
    }
  }, [props.containerApi, props.api])

  const handleDock = () => {
    if (!isFloatingGroup) {
      return
    }
    const docked = dockFloatingGroupToGrid(props.group, props.containerApi)
    if (docked) {
      setDockButtonDismissed(true)
      props.persistLayout?.()
    } else {
      notifications.error("Could not dock this panel into the dashboard. Please try again.")
    }
  }

  const handleToggleMaximize = () => {
    const activePanelId = props.activePanel?.id
    const pendingFloatRestore = getFloatingDockMaximizeSession(props.group, activePanelId)

    if (pendingFloatRestore && gridMaximized) {
      props.api.exitMaximized()
      const restored = restoreFloatingGroupAfterMaximizedDock(
        props.group,
        props.containerApi,
        activePanelId,
      )
      if (!restored) {
        notifications.error("Could not restore this panel to a floating window. Please try again.")
      }
      props.persistLayout?.()
      return
    }

    if (isFloatingGroup) {
      const dockedIntoGrid = dockFloatingGroupForMaximize(props.group, props.containerApi)
      if (dockedIntoGrid) {
        const panelId = props.activePanel?.id ?? props.panels[0]?.id
        const panelAfterDock = panelId != null ? props.containerApi.getPanel(panelId) : undefined
        if (panelAfterDock) {
          props.containerApi.maximizeGroup(panelAfterDock)
        } else {
          notifications.error("Could not maximize this panel after docking. Please try again.")
        }
      } else {
        notifications.error("Could not move this panel into the dashboard to maximize. Please try again.")
      }
      props.persistLayout?.()
      return
    }

    if (gridMaximized) {
      props.api.exitMaximized()
    } else {
      props.api.maximize()
    }
    props.persistLayout?.()
  }

  const expanded = gridMaximized
  const showDockButton = isFloatingGroup && !dockButtonDismissed

  return (
    <>
      {showDockButton && (
        <OPTooltip title="Dock into dashboard">
          <span>
            <IconButton onClick={handleDock} aria-label="Dock into dashboard">
              <Dock />
            </IconButton>
          </span>
        </OPTooltip>
      )}
      <OPTooltip title={expanded ? "Restore" : "Maximize"}>
        <span>
          <IconButton
            onClick={handleToggleMaximize}
            aria-label={expanded ? "Restore window" : "Maximize window"}
          >
            {expanded ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </span>
      </OPTooltip>
      {showDockButton && props.closePanel && (
        <OPTooltip title="Close panel">
          <span>
            <IconButton
              onClick={() => {
                const panelId = props.activePanel?.id ?? props.panels[0]?.id
                if (panelId != null && props.closePanel) {
                  props.closePanel(panelId, removeRequiresConfirmation)
                }
              }}
              aria-label="Close panel"
            >
              <Close />
            </IconButton>
          </span>
        </OPTooltip>
      )}
    </>
  )
}
